import { useRef, useState } from "react";
import { X, Download, Printer, Loader2 } from "lucide-react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useSettings } from "@/hooks/useSettings";
import { resolveImage } from "@/lib/api";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const monthLabel = (key) => {
  if (!key) return "—";
  const [y, m] = key.split("-");
  if (!m) return key;
  return `${MONTHS[Number(m) - 1]} ${y}`;
};
const dash = (v) => (v === undefined || v === null || v === "" ? "—" : v);

const gold = "#C9A227";
const ink = "#1a1a1a";

function Line({ label, value, strong }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "9px 0", borderBottom: "1px solid #eee" }}>
      <span style={{ color: "#666", fontSize: 13 }}>{label}</span>
      <span style={{ color: ink, fontSize: 13, fontWeight: strong ? 700 : 500 }}>{value}</span>
    </div>
  );
}

export function SalarySlip({ record, onClose }) {
  const s = useSettings();
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const brand = s.site_name || "Golden Live";

  const downloadPDF = async () => {
    if (!ref.current) return;
    setBusy(true);
    try {
      const canvas = await html2canvas(ref.current, { scale: 2, backgroundColor: "#ffffff", useCORS: true });
      const img = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const w = pdf.internal.pageSize.getWidth();
      const h = (canvas.height * w) / canvas.width;
      pdf.addImage(img, "PNG", 0, 0, w, Math.min(h, pdf.internal.pageSize.getHeight()));
      pdf.save(`${brand.replace(/\s+/g, "")}-Salary-${record.host_name}-${record.salary_month || ""}.pdf`);
    } finally {
      setBusy(false);
    }
  };

  const print = () => {
    const html = ref.current?.outerHTML || "";
    const win = window.open("", "_blank", "width=800,height=1000");
    if (!win) return;
    win.document.write(`<html><head><title>Salary Slip</title></head><body style="margin:0;background:#fff;">${html}</body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); }, 400);
  };

  const statusColor = record.status === "Paid" ? "#1EA150" : "#C7801A";

  return (
    <div className="fixed inset-0 z-[110] flex items-start justify-center overflow-y-auto bg-black/80 p-4 backdrop-blur-sm" data-testid="salary-slip-modal">
      <div className="my-6 w-full max-w-[640px]">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-white">Salary Slip</h3>
          <div className="flex items-center gap-2">
            <button onClick={downloadPDF} disabled={busy} data-testid="slip-download-btn"
              className="inline-flex items-center gap-2 rounded-lg gold-gradient px-4 py-2 text-sm font-bold text-obsidian disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} PDF
            </button>
            <button onClick={print} data-testid="slip-print-btn"
              className="inline-flex items-center gap-2 rounded-lg border border-gold/40 px-4 py-2 text-sm font-bold text-gold hover:bg-gold/10">
              <Printer className="h-4 w-4" /> Print
            </button>
            <button onClick={onClose} data-testid="slip-close-btn" className="rounded-lg p-2 text-slate-300 hover:bg-white/10"><X className="h-5 w-5" /></button>
          </div>
        </div>

        {/* The document (inline styles => self-contained for PDF & print) */}
        <div ref={ref} style={{ background: "#fff", color: ink, fontFamily: "Arial, Helvetica, sans-serif", width: "100%", boxSizing: "border-box" }}>
          <div style={{ background: "#0A0A0C", padding: "22px 28px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {s.logo_url ? (
                <img src={resolveImage(s.logo_url)} alt="logo" crossOrigin="anonymous" style={{ height: 42, width: 42, borderRadius: 8, objectFit: "cover" }} />
              ) : (
                <div style={{ height: 42, width: 42, borderRadius: 8, background: gold, display: "flex", alignItems: "center", justifyContent: "center", color: "#0A0A0C", fontWeight: 800, fontSize: 22 }}>★</div>
              )}
              <div>
                <div style={{ color: gold, fontSize: 22, fontWeight: 800, letterSpacing: 1 }}>{brand}</div>
                <div style={{ color: "#bbb", fontSize: 11, letterSpacing: 2, textTransform: "uppercase" }}>Official Host Salary Slip</div>
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ color: "#fff", fontSize: 13, fontWeight: 700 }}>{monthLabel(record.salary_month)}</div>
              <div style={{ color: "#888", fontSize: 11 }}>Salary date: 15th</div>
            </div>
          </div>

          <div style={{ height: 4, background: gold }} />

          <div style={{ padding: "24px 28px" }}>
            {/* Host block */}
            <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 18 }}>
              <div>
                <div style={{ fontSize: 11, color: "#999", textTransform: "uppercase", letterSpacing: 1 }}>Host</div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>{dash(record.host_name)}</div>
                <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>User ID: {dash(record.user_id)}{record.agency ? ` · ${record.agency}` : ""}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <span style={{ display: "inline-block", padding: "5px 14px", borderRadius: 20, background: statusColor, color: "#fff", fontSize: 12, fontWeight: 700 }}>
                  {record.status === "Paid" ? "PAID" : "PENDING / UNPAID"}
                </span>
                {record.payment_date && <div style={{ fontSize: 11, color: "#666", marginTop: 6 }}>Paid on {record.payment_date}</div>}
              </div>
            </div>

            {/* Two columns: performance + salary */}
            <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
              <div style={{ flex: "1 1 240px" }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: gold, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Performance</div>
                <Line label="Total Live Days" value={dash(record.total_live_days)} />
                <Line label="Total Live Hours" value={dash(record.total_live_hours)} />
                <Line label="Avg Daily Hours" value={dash(record.avg_daily_live_hours)} />
                <Line label="Days 4+ hrs" value={dash(record.days_4h)} />
                <Line label="Days 2+ hrs" value={dash(record.days_2h)} />
                <Line label="Total Reward Coins" value={dash(record.total_reward_coins)} />
                <Line label="Monthly Target" value={record.target_status || "—"} strong />
              </div>
              <div style={{ flex: "1 1 240px" }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: gold, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Salary</div>
                <Line label="Basic Salary" value={dash(record.basic_salary)} />
                <Line label="Bonus" value={dash(record.bonus)} />
                <Line label="Other Adjustment" value={dash(record.adjustment)} />
                <div style={{ display: "flex", justifyContent: "space-between", padding: "12px 0", marginTop: 4, borderTop: `2px solid ${gold}` }}>
                  <span style={{ fontWeight: 800, fontSize: 15 }}>TOTAL SALARY</span>
                  <span style={{ fontWeight: 800, fontSize: 15, color: ink }}>{dash(record.total_salary)}</span>
                </div>
              </div>
            </div>

            {record.notes && (
              <div style={{ marginTop: 18, padding: 12, background: "#faf6e9", borderRadius: 8, fontSize: 12, color: "#555" }}>
                <b>Notes:</b> {record.notes}
              </div>
            )}

            <div style={{ marginTop: 26, display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
              <div style={{ fontSize: 10, color: "#999", maxWidth: 320 }}>
                This is a system-generated salary slip issued by {brand}. Policy: 30-day period · 120 live hours/month · 4 hrs daily target · 5,000 coins for 2 hrs/day.
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ width: 150, borderTop: "1px solid #999", paddingTop: 4, fontSize: 11, color: "#666" }}>Authorised Signature</div>
              </div>
            </div>
          </div>

          <div style={{ background: "#0A0A0C", color: "#777", fontSize: 10, textAlign: "center", padding: "10px 0" }}>
            © {new Date().getFullYear()} {brand} · Official Host Program
          </div>
        </div>
      </div>
    </div>
  );
}
